import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import groupBy from 'lodash/groupBy';
import getNested from '../utils/getNested';
import { addHours, isBefore, addMinutes, isSameDay } from 'date-fns';
import { HttpInstance } from '../utils/http';
import { createConfiguration } from '../types/configuration';
import { createUser, User } from '../types/user';
import { FetchBookingOptions } from '../types/salon';
import { createService, Service } from '../types/service';
import { createStylist, FetchStylistOptions, FetchDatesOptions, StylistType, FetchTimesOptions } from '../types/stylist';
import { createCheckinData } from '../types/checkin';
import { createBookingData, createBookingItem } from '../types/booking';
import { createReward } from '../types/reward';

dayjs.extend(customParseFormat)

class BookingService {
  http: HttpInstance;

  constructor(http: HttpInstance) {
    this.http = http;
    return this;
  }

  fetchBookingHistory(salonId: number, phoneNumber: string) {
    const url = `api/salon/check-upcoming-booking?id=${salonId}&phone_number=${phoneNumber}`;
    return this.http.get(url)
      .then(response => {
        const results = getNested(response, 'data.data', []) || [];
        return results.map(createBookingItem);
      })
      .catch(() => ([]));
  }

  fetchSettings(salonId: number | string) {
    const url = `api/salon/config-booking?id=${salonId}`;
    return this.http.get(url)
      .then(response => {
        const configuration = createConfiguration(getNested(response.data, 'data', {}));
        return configuration;
      })
      .catch(() => ({}));
  }

  fetchUserProfile(options) {
    const { salonId, profileId, accessToken } = options;
    const url = `api/salon/clients/detail/${salonId}?access_token=${accessToken}&client_id=${profileId}`;

    return this.http.get(url)
      .then(response => {
        const result = getNested(response, 'data.data', {}) || {};
        const { upcoming_appointment, client_data, checkin_data, reward_history } = result;

        const clientData = createUser(client_data || {});
        const checkinData = (checkin_data || []).map(createCheckinData);
        const rewardHistory = (reward_history || []).map(createReward);
        const upcomingBooking = upcoming_appointment ? createBookingData(upcoming_appointment) : null;

        return {
          clientData,
          checkinData,
          rewardHistory,
          upcomingBooking,
        };
      })
      ;
  }

  fetchUsers(options) {
    const { salonId, phoneNumber, accessToken } = options;
    const url = `api/salon/consumer/search?keyword=${phoneNumber}${salonId ? `&id=${salonId}` : ''}${accessToken ? `&access_token=${accessToken}` : ''}`;

    return this.http.get(url)
      .then(response => {
        const suggestions = (response.data || []).slice(0, 4);
        return suggestions.map(blob => {
          return createUser({
            ...blob,
            can_view_profile: true,
            profile_id: blob.client_id,
          });
        });
      })
      .catch(() => ([]));
  }

  fetchSalonServices(salonId: number, stylistId?: number) {
    const url = `api/salon/services?id=${salonId}&v=2${stylistId ? `&stylist_id=${stylistId}` : ''}`;
    return this.http.get(url)
      .then(response => {
        let serviceGroups = [];

        try {
          serviceGroups = this.decorateServiceGroups(response, !!stylistId);
        } catch (error) {}

        return serviceGroups;
      })
      .catch(() => ([]));
  }

  private decorateServiceGroups(response, hasStylistId: boolean) {
    const mainCates = getNested(response, 'data.data', []);

    return mainCates.map(cate => {
      const cateName = cate.name;
      const categoryKey = `${cate.service_id}-${String(cateName).toLowerCase().split(' ').join('-')}-${cate.order}`;
      const sortedSubCates = sortBy(cate.service_groups, [subCate => subCate.order]);

      return {
        key: categoryKey,
        name: cateName,
        type: 'category',
        data: sortedSubCates.map(subCate => {
          const subCateKey = `${categoryKey}-${String(subCate.name).toLowerCase().split(' ').join('-')}-${subCate.order}`;

          return {
            key: subCateKey,
            name: subCate.category,
            type: 'sub-category',
            data: (subCate.services || []).map((rawService: any) => {
              const groupServiceKey = `${subCateKey}-${String(rawService.name).toLowerCase().split(' ').join('-')}-${rawService.id}`;
              const serviceObj = createService({
                ...rawService,
                key: groupServiceKey,
                category: cateName,
                categoryKey,
                subCategory: subCate.name,
                subCategoryKey: subCateKey,
              }, hasStylistId);

              return { ...serviceObj, type: 'service' };
            }),
          };
        }),
      };
    });
  }

  fetchStylists(options: FetchStylistOptions, selectedServices: any = []) {
    const serviceIds = selectedServices.map(service => service.id);
    const { salonId, stylistId, bookingId, accessToken, date, time } = options;
    const url = `api/salon/stylists?id=${ salonId }&service_ids=${ serviceIds.join(',') }${date ? `&available_match_day=${date}` : ''}${time ? `&available_match_time=${time}` : ''}${stylistId ? `&selected_stylist_id=${stylistId}` : ''}${bookingId ? `&edit_booking_id=${bookingId}` : ''}${accessToken ? `&access_token=${accessToken}` : ''}`;

    return this.http.get(url)
      .then(response => {
        const selectedStylist = getNested(response, 'data.data.selected_stylist');
        const stylists = getNested(response, 'data.data.stylists');

        if (stylistId && selectedStylist && !(stylists || []).find(stylist => stylist.stylist_id === stylistId)) {
          stylists.unshift(selectedStylist);
        }

        return stylists.map(stylist => createStylist(stylist, selectedServices));
      })
      .catch(() => ([]))
      ;
  }

  fetchStylistSettings(options: FetchDatesOptions, selectedServices = []) {
    const { salonId, bookingId, accessToken } = options;
    const stylistIds = uniq(selectedServices.map((service: Service) => service.stylistId))
      .filter(stylistId => stylistId !== StylistType.NoPreference);

    if (stylistIds.length) {
      const editParams = bookingId ? `&edit_booking_id=${bookingId}${accessToken ? `&access_token=${accessToken}` : ''}` : '';
      const url = `api/salon/stylists/available-day?id=${salonId}&stylist_ids=${stylistIds.join(',')}${editParams}`;
        return this.http.get(url)
          .then(response => {
            const { mon, tue, wed, thu, fri, sat, sun } = getNested(response, 'data.data', {});

            return {
              canBookOnMonday: !!mon,
              canBookOnTuesday: !!tue,
              canBookOnWednesday: !!wed,
              canBookOnThursday: !!thu,
              canBookOnFriday: !!fri,
              canBookOnSaturday: !!sat,
              canBookOnSunday: !!sun,
            };
          });
    }

    return Promise.resolve({});
  }

  private isValidHour(date: any, dateToCmpare) {
    const isBeforeMatched = isBefore(date, dateToCmpare);
    const isSameDayMatched = isSameDay(date, dateToCmpare) && date <= dateToCmpare;

    return isBeforeMatched || isSameDayMatched;
  }

  private excludePastTimes(businessHours: string[], options: FetchTimesOptions) {
    const { selectedDate, selectedTime, minHours = 0, schedule, duration = 0 } = options;

    if (minHours > 0 || !!schedule) {
      const { start, end } = schedule || {};
      const minScheduleDate = !!start ? dayjs(`${selectedDate} ${start}`, 'YYYY-MM-DD HH:mm A').valueOf() : null;
      const maxScheduleDate = !!end ? dayjs(`${selectedDate} ${end}`, 'YYYY-MM-DD HH:mm A').valueOf() : null;
      const minBeforeDate = dayjs(addHours(new Date(), minHours)).valueOf();

      const minDate = minScheduleDate && this.isValidHour(minBeforeDate, minScheduleDate) ? minScheduleDate : minBeforeDate;
      const businessHoursFiltered = businessHours
        .map(businessHour => {
          const timeStr = String(`0${businessHour}`);
          return timeStr.substring(timeStr.length - 8);
        })
        .filter(businessHour => {
          const businessDateTime = dayjs(`${selectedDate} ${businessHour}`, 'YYYY-MM-DD HH:mm A').valueOf();
          const isMinTimeMatched = this.isValidHour(minDate, businessDateTime);
          const isMaxTimeMatched = maxScheduleDate ? this.isValidHour(addMinutes(businessDateTime, duration), maxScheduleDate) : true;

          return isMinTimeMatched && isMaxTimeMatched;
        });

      if (!!selectedTime && businessHoursFiltered.indexOf(selectedTime) === -1) {
        return businessHoursFiltered.concat([selectedTime]);
      }

      return businessHoursFiltered;
    }

    return businessHours;
  }

  private getBusinessHours(options: FetchTimesOptions) {
    const businessHours = [
      "1:00 AM",
      "1:15 AM",
      "1:30 AM",
      "1:45 AM",
      "2:00 AM",
      "2:15 AM",
      "2:30 AM",
      "2:45 AM",
      "3:00 AM",
      "3:15 AM",
      "3:30 AM",
      "3:45 AM",
      "4:00 AM",
      "4:15 AM",
      "4:30 AM",
      "4:45 AM",
      "5:00 AM",
      "5:15 AM",
      "5:30 AM",
      "5:45 AM",
      "6:00 AM",
      "6:15 AM",
      "6:30 AM",
      "6:45 AM",
      "7:00 AM",
      "7:15 AM",
      "7:30 AM",
      "7:45 AM",
      "8:00 AM",
      "8:15 AM",
      "8:30 AM",
      "8:45 AM",
      '9:00 AM',
      '9:15 AM',
      '9:30 AM',
      '9:45 AM',
      "10:00 AM",
      "10:15 AM",
      "10:30 AM",
      "10:45 AM",
      "11:00 AM",
      "11:15 AM",
      "11:30 AM",
      "11:45 AM",
      "12:00 PM",
      "12:15 PM",
      "12:30 PM",
      "12:45 PM",
      "1:00 PM",
      "1:15 PM",
      "1:30 PM",
      "1:45 PM",
      "2:00 PM",
      "2:15 PM",
      "2:30 PM",
      "2:45 PM",
      "3:00 PM",
      "3:15 PM",
      "3:30 PM",
      "3:45 PM",
      "4:00 PM",
      "4:15 PM",
      "4:30 PM",
      "4:45 PM",
      "5:00 PM",
      "5:15 PM",
      "5:30 PM",
      "5:45 PM",
      "6:00 PM",
      "6:15 PM",
      "6:30 PM",
      "6:45 PM",
      "7:00 PM",
      "7:15 PM",
      "7:30 PM",
      "7:45 PM",
      "8:00 PM",
      "8:15 PM",
      "8:30 PM",
      "8:45 PM",
      "9:00 PM",
      "9:15 PM",
      "9:30 PM",
      "9:45 PM",
      "10:00 PM",
      "10:15 PM",
      "10:30 PM",
      "10:45 PM",
      "11:00 PM",
      "11:15 PM",
      "11:30 PM",
      "11:45 PM",
    ];

    return this.excludePastTimes(businessHours, options);
  };

  private getServiceStylistsMapping(services = [], stylistIds = []) {
    let serviceIds: any = [];

    stylistIds.forEach(stylistId => {
      const selectedServiceIds: number[] = services
        .filter((service: Service) => service.stylistId === stylistId)
        .map((service: Service) => service.id) as any;
      serviceIds = serviceIds.concat(selectedServiceIds);
    });

    return { stylistIds, serviceIds };
  }

  private getAvailableHours(options: FetchTimesOptions, services = []) {
    const stylistIds = uniq(services.map((service: Service) => service.stylistId))
      .filter(stylistId => stylistId !== StylistType.NoPreference);

    if (stylistIds.length) {
      const mapping = this.getServiceStylistsMapping(services, stylistIds);
      const { salonId, selectedDate, bookingId, accessToken } = options;
      const editParams = bookingId ? `&edit_booking_id=${bookingId}${accessToken ? `&access_token=${accessToken}` : ''}` : '';
      const url = `api/salon/stylists/available-day?id=${salonId}&stylist_ids=${mapping.stylistIds.join(',')}&service_ids=${mapping.serviceIds.join(',')}&available_match_day=${selectedDate}${editParams}`;

      return this.http.get(url)
        .then(response => {
          const businessHours = getNested(response, 'data.data', []);
          return this.excludePastTimes(businessHours, options);
        })
        .catch(() => ([]));
    }

    return this.getBusinessHours(options);
  };

  fetchAvailableTimes(options: FetchTimesOptions, services = []) {
    const { canBookAnyTime } = options;

    if (canBookAnyTime) {
      const availableTimes = this.getBusinessHours(options);
      return Promise.resolve(availableTimes);
    }

    return this.getAvailableHours(options, services);
  }

  createBooking(data) {
    const url = 'api/user/booking-apointment/salon/?call=one_page_booking';
    return this.http.post(url, data);
  }

  updateBooking(data: any, accessToken: string) {
    const url = `api/salon/edit-booking-appointment?access_token=${accessToken}`;
    return this.http.post(url, data);
  }

  private createService(serviceBlob: any, stylistBlob: any): Service{
    return {
      id: Number(serviceBlob.service_id),
      name: serviceBlob.service_name,
      stylistId: stylistBlob.stylist_id,
      stylistName: stylistBlob.stylist_name,
    } as Service;
  }

  private formatClientServices(blobs: any): Service[] {
    const stylistGroups = groupBy(blobs, blob => (blob.stylist_id));
    const stylistGroupKeys = Object.keys(stylistGroups);
    let clientServices: Service[] = [];

    // same stylist
    if (stylistGroupKeys.length === 1) {
      const stylistBlob = blobs[0];

      (blobs || []).forEach(blob => {
        (blob.service_list || []).forEach(serviceBlob => {
          clientServices = clientServices.concat([this.createService(serviceBlob, stylistBlob)]);
        });
      });

      return clientServices;
    }

    // different stlists
    (stylistGroupKeys || []).forEach(groupKey => {
      const stylistBlob = stylistGroups[groupKey][0] || {};
      (stylistBlob.service_list || []).forEach(serviceBlob => {
        clientServices = clientServices.concat([this.createService(serviceBlob, stylistBlob)]);
      });
    });

    return clientServices;
  }

  private formatClient(blob: any, index: number): User {
    return createUser({
      ...blob,
      mobile_phone: blob.phone,
      profile_id: Number(blob.client_id),
      can_delete: index > 0,
      can_view_profile: !!blob.client_id,
      can_select_service: true,
      can_select_stylist: true,
      can_select_date_time: index === 0,
      booking_services: this.formatClientServices(blob.stylist_data || []),
    });
  }

  fetchBookingById(options: FetchBookingOptions) {
    const { salonId, bookingId, accessToken } = options;
    const url = `api/salon/appointment/detail/${salonId}?booking_id=${bookingId}&access_token=${accessToken}`;

    return this.http.get(url)
      .then(response => {
        const { user, stylists, clients, ...bookingDetail } = getNested(response, 'data.data', {});
        const {
          user_id,
          client_id,
          booking_date,
          booking_time,
          special_attention,
        } = bookingDetail || {};

        // format services
        let selectedServices: any[] = [];
        stylists.forEach(stylistData => {
          const { stylist_id, stylist_name, service_list } = stylistData;
          (service_list || []).forEach(serviceData => {
            const service = {
              id: Number(serviceData.service_id),
              name: serviceData.service_name,
              stylistId: stylist_id,
              stylistName: stylist_name,
            };
            selectedServices = selectedServices.concat([service]);
          });
        });

        const bookingClients = (clients || [])
          .filter(client => (client.stylist_data || []).length > 0)
          .map((blob, index) => {
            return this.formatClient(
              {
                ...blob,
                user_id: blob.user_id || (index === 0 ? user_id : index + 1),
                client_id: blob.client_id || (index === 0 ? client_id : null),
                booking_date: booking_date ? dayjs(`${booking_date}`, 'YYYY-MM-DD').toDate() : null,
                booking_time,
              },
              index,
            );
          })
          ;

        // format user
        const bookingUser = {
          id: Number(user.id),
          email: user.email,
          firstName: user.first_name,
          lastName: user.last_name,
          phoneNumber: user.mobile_phone,

          canDelete: false,
          profileId: Number(client_id),
          canViewProfile: true,
          canSelectService: true,
          canSelectStylist: true,
          canSelectDateTime: true,

          selectedServices,
          selectedTime: booking_time,
          selectedDate: booking_date ? new Date(booking_date) : null,
        };

        return {
          id: bookingId,
          bookingUser,
          bookingClients,
          bookingNotes: special_attention,
        };
      })
      .catch(() => ({}));
  }
}

export default (http: HttpInstance) => new BookingService(http);
